import { useEffect, useState, useRef } from "react";
import api, { API } from "../lib/api";
import { getToken } from "../lib/tokenStorage";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { UserCircle2, Upload, IdCard, CalendarDays, Phone, Save, Mail, Lock, KeyRound, Bell } from "lucide-react";

const ID_TYPES = ["Aadhar Card", "Passport", "Driver's License", "PAN Card", "Voter ID", "Other"];

export default function Profile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({ name: "", dob: "", phone: "", id_doc_type: "Aadhar Card", date_format: "DD/MM/YYYY" });
  const [savingPic, setSavingPic] = useState(false);
  const [savingId, setSavingId] = useState(false);
  const [emailForm, setEmailForm] = useState({ new_email: "", current_password: "" });
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [emailBusy, setEmailBusy] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);
  const [notifGroups, setNotifGroups] = useState([]);
  const [notifBusy, setNotifBusy] = useState(false);
  const picRef = useRef();
  const idRef = useRef();

  const load = async () => {
    const { data } = await api.get("/me/profile");
    setProfile(data);
    setForm(f => ({
      ...f,
      name: data.name || "",
      dob: data.dob || "",
      phone: data.phone || "",
      id_doc_type: data.id_doc_type || "Aadhar Card",
      date_format: data.date_format || "DD/MM/YYYY",
    }));
    try {
      const { data: np } = await api.get("/me/notif-prefs");
      setNotifGroups(np.groups || []);
    } catch (err) { console.debug("notif prefs load skipped:", err?.message); }
  };
  useEffect(() => { load(); }, []);

  const toggleNotifGroup = async (key) => {
    const next = notifGroups.map(g => g.key === key ? { ...g, on: !g.on } : g);
    setNotifGroups(next);
    setNotifBusy(true);
    try {
      const prefs = Object.fromEntries(next.map(g => [g.key, g.on]));
      await api.patch("/me/notif-prefs", { prefs });
      toast.success("Notification preferences saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
      setNotifGroups(notifGroups); // rollback on error
    } finally { setNotifBusy(false); }
  };

  const token = getToken();
  const picUrl = profile?.profile_pic_id ? `${API}/files/${profile.profile_pic_id}?auth=${token}` : null;

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.id;
  };

  const onPicChange = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 25 * 1024 * 1024) { toast.error("Photo too large (max 25 MB)"); if (picRef.current) picRef.current.value = ""; return; }
    setSavingPic(true);
    const t = toast.loading("Uploading photo…");
    try {
      const id = await uploadFile(f);
      await api.patch("/me/profile", { profile_pic_id: id });
      toast.success("Profile picture updated", { id: t });
      await load();
    } catch (err) {
      // Surface the ACTUAL server reason (415 for unsupported type,
      // 413 for too large, network error, etc.) so people stop tapping
      // Upload wondering why nothing happens.
      const detail = err?.response?.data?.detail || err?.message || "Upload failed";
      toast.error(detail, { id: t });
    }
    finally { setSavingPic(false); if (picRef.current) picRef.current.value = ""; }
  };

  const onIdChange = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 25 * 1024 * 1024) { toast.error("File too large (max 25 MB)"); if (idRef.current) idRef.current.value = ""; return; }
    setSavingId(true);
    const t = toast.loading("Uploading document…");
    try {
      const id = await uploadFile(f);
      await api.patch("/me/profile", { id_doc_id: id, id_doc_type: form.id_doc_type });
      toast.success("ID document uploaded", { id: t });
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Upload failed";
      toast.error(detail, { id: t });
    }
    finally { setSavingId(false); if (idRef.current) idRef.current.value = ""; }
  };

  const saveInfo = async () => {
    const updated = await api.patch("/me/profile", { name: form.name, dob: form.dob, phone: form.phone, id_doc_type: form.id_doc_type, date_format: form.date_format });
    toast.success("Profile saved");
    // If name or date_format changed, refresh auth context so the app picks up the new formatting.
    if (updated?.data) {
      window.dispatchEvent(new CustomEvent("aii:profile_updated"));
    }
    await load();
  };

  const changeEmail = async () => {
    if (!emailForm.new_email || !emailForm.current_password) { toast.error("Fill both fields"); return; }
    setEmailBusy(true);
    try {
      await api.post("/auth/change-email", { new_email: emailForm.new_email.trim().toLowerCase(), current_password: emailForm.current_password });
      toast.success("Email updated · replies from your outreach will now come to this inbox");
      setEmailForm({ new_email: "", current_password: "" });
      window.dispatchEvent(new CustomEvent("aii:profile_updated"));
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setEmailBusy(false); }
  };

  const changePassword = async () => {
    if (!pwForm.current_password || !pwForm.new_password) { toast.error("Fill both password fields"); return; }
    if (pwForm.new_password !== pwForm.confirm) { toast.error("New passwords do not match"); return; }
    if (pwForm.new_password.length < 6) { toast.error("Password must be 6+ characters"); return; }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pwForm.current_password, new_password: pwForm.new_password });
      toast.success("Password changed");
      setPwForm({ current_password: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setPwBusy(false); }
  };

  if (!profile) return <div className="p-8 text-center text-[color:var(--aii-muted)]">Loading…</div>;

  return (
    <div className="space-y-5 max-w-3xl">
      <div>
        <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Account</div>
        <h1 className="text-2xl font-semibold flex items-center gap-2"><UserCircle2 className="text-brand" /> My Profile</h1>
      </div>

      {/* Profile Card */}
      <div className="aii-card p-6">
        <div className="flex items-center gap-5">
          <div className="relative">
            {picUrl ? (
              <img src={picUrl} alt="avatar" className="w-24 h-24 rounded-full object-cover border-4 border-[color:var(--aii-green-tint)]" />
            ) : (
              <div className="w-24 h-24 rounded-full bg-[color:var(--aii-green-tint)] text-[color:var(--aii-green-dark)] font-bold flex items-center justify-center font-display text-3xl">{user.name[0]}</div>
            )}
            <input ref={picRef} type="file" accept="image/*" className="hidden" onChange={onPicChange} data-testid="pf-pic-input" />
            <button data-testid="pf-pic-btn" onClick={() => picRef.current?.click()} disabled={savingPic}
              className="absolute -bottom-1 -right-1 bg-brand text-white rounded-full p-2 hover:bg-brand-dark disabled:opacity-50">
              <Upload size={12} />
            </button>
          </div>
          <div>
            <div className="text-lg font-semibold">{user.name}</div>
            <div className="text-sm text-[color:var(--aii-muted)]">{user.email}</div>
            <span className="pill pill-green mt-1 inline-block">{user.role}</span>
          </div>
        </div>
      </div>

      {/* Personal info */}
      <div className="aii-card p-6 space-y-4">
        <h3 className="font-semibold flex items-center gap-2"><CalendarDays size={16} className="text-brand" /> Personal Information</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-[color:var(--aii-text-2)] font-semibold">Date of birth</label>
            <input data-testid="pf-dob" type="date" value={form.dob} onChange={e => setForm(f => ({ ...f, dob: e.target.value }))}
              className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[color:var(--aii-text-2)] font-semibold flex items-center gap-1"><Phone size={11} /> Phone</label>
            <input data-testid="pf-phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
              className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm" placeholder="+91 ..." />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs uppercase tracking-wider text-[color:var(--aii-text-2)] font-semibold">Date format preference</label>
            <div className="mt-1 flex flex-wrap gap-2">
              {[
                { v: "DD/MM/YYYY", hint: "India / UK / most of the world" },
                { v: "MM/DD/YYYY", hint: "United States" },
              ].map(opt => (
                <button key={opt.v} type="button"
                  data-testid={`pf-datefmt-${opt.v}`}
                  onClick={() => setForm(f => ({ ...f, date_format: opt.v }))}
                  className={`text-sm px-3 py-2 rounded-md border flex-1 min-w-[220px] text-left transition ${form.date_format === opt.v ? "bg-brand text-white border-brand" : "bg-white border-[color:var(--aii-border)] hover:bg-[color:var(--aii-green-tint)]"}`}>
                  <div className="font-semibold">{opt.v}</div>
                  <div className={`text-[11px] ${form.date_format === opt.v ? "text-white/80" : "text-[color:var(--aii-muted)]"}`}>{opt.hint}</div>
                </button>
              ))}
            </div>
            <p className="text-[11px] text-[color:var(--aii-muted)] mt-2">Applied everywhere dates appear — bookings, arrivals, C-forms, reports.</p>
          </div>
        </div>
        <div className="flex justify-end">
          <button data-testid="pf-save" onClick={saveInfo} className="bg-brand text-white font-semibold px-4 py-2 rounded-md hover:bg-brand-dark flex items-center gap-1.5"><Save size={14} /> Save</button>
        </div>
      </div>

      {/* ID document */}
      <div className="aii-card p-6 space-y-4">
        <h3 className="font-semibold flex items-center gap-2"><IdCard size={16} className="text-brand" /> ID Document</h3>
        <p className="text-sm text-[color:var(--aii-text-2)]">Upload a scan of your government-issued ID (Aadhar / Passport / etc.). Only Founder can view it.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-[color:var(--aii-text-2)] font-semibold">ID type</label>
            <select data-testid="pf-id-type" value={form.id_doc_type} onChange={e => setForm(f => ({ ...f, id_doc_type: e.target.value }))}
              className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm">
              {ID_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="flex items-end gap-2">
            <input ref={idRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={onIdChange} data-testid="pf-id-input" />
            <button data-testid="pf-id-upload" onClick={() => idRef.current?.click()} disabled={savingId}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2 border border-[color:var(--aii-border)] bg-white rounded-md hover:bg-[color:var(--aii-green-tint)] text-sm disabled:opacity-50">
              <Upload size={14} /> {profile.id_doc_id ? "Replace document" : "Upload document"}
            </button>
          </div>
        </div>
        {profile.id_doc_id && (
          <div className="text-sm text-emerald-700">
            <a href={`${API}/files/${profile.id_doc_id}?auth=${token}`} target="_blank" rel="noreferrer" className="underline">
              View uploaded {profile.id_doc_type || "document"}
            </a>
          </div>
        )}
      </div>

      {/* Change login email */}
      <div className="aii-card p-6 space-y-4">
        <h3 className="font-semibold flex items-center gap-2"><Mail size={16} className="text-brand"/> Login email</h3>
        <p className="text-sm text-[color:var(--aii-text-2)]">Your current login is <b>{user.email}</b>. Update it and this new inbox will receive replies to any outreach you send from the app (partners, leads).</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-[color:var(--aii-text-2)] font-semibold">New email</label>
            <input data-testid="pf-new-email" type="email" value={emailForm.new_email} onChange={e=>setEmailForm(f=>({...f,new_email:e.target.value}))} className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm" placeholder="you@personal.com"/>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[color:var(--aii-text-2)] font-semibold">Current password</label>
            <input data-testid="pf-email-current-pw" type="password" value={emailForm.current_password} onChange={e=>setEmailForm(f=>({...f,current_password:e.target.value}))} className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/>
          </div>
        </div>
        <div className="flex justify-end">
          <button data-testid="pf-email-save" onClick={changeEmail} disabled={emailBusy} className="bg-brand text-white font-semibold px-4 py-2 rounded-md hover:bg-brand-dark disabled:opacity-50 flex items-center gap-1.5"><Save size={14}/> {emailBusy ? "Saving…" : "Update email"}</button>
        </div>
      </div>

      {/* Change password */}
      <div className="aii-card p-6 space-y-4">
        <h3 className="font-semibold flex items-center gap-2"><Lock size={16} className="text-brand"/> Password</h3>
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-[color:var(--aii-text-2)] font-semibold">Current</label>
            <input data-testid="pf-current-pw" type="password" value={pwForm.current_password} onChange={e=>setPwForm(f=>({...f,current_password:e.target.value}))} className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[color:var(--aii-text-2)] font-semibold">New</label>
            <input data-testid="pf-new-pw" type="password" value={pwForm.new_password} onChange={e=>setPwForm(f=>({...f,new_password:e.target.value}))} className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[color:var(--aii-text-2)] font-semibold">Confirm</label>
            <input data-testid="pf-confirm-pw" type="password" value={pwForm.confirm} onChange={e=>setPwForm(f=>({...f,confirm:e.target.value}))} className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/>
          </div>
        </div>
        <div className="flex justify-end">
          <button data-testid="pf-pw-save" onClick={changePassword} disabled={pwBusy} className="bg-brand text-white font-semibold px-4 py-2 rounded-md hover:bg-brand-dark disabled:opacity-50 flex items-center gap-1.5"><KeyRound size={14}/> {pwBusy ? "Saving…" : "Change password"}</button>
        </div>
      </div>

      {/* Notification preferences (per-topic mute) */}
      {notifGroups.length > 0 && (
        <div className="aii-card p-6 space-y-4" data-testid="pf-notif-prefs">
          <div>
            <h3 className="font-semibold flex items-center gap-2"><Bell size={16} className="text-brand"/> Notification preferences</h3>
            <p className="text-sm text-[color:var(--aii-text-2)] mt-1">Mute noisy channels without cutting all alerts. Emergency broadcasts, team messages, and login events always deliver.</p>
          </div>
          <div className="space-y-2">
            {notifGroups.map(g => (
              <label key={g.key} className="flex items-center justify-between gap-3 px-3 py-2.5 border border-[color:var(--aii-border)] rounded-md hover:bg-[color:var(--aii-green-tint)]/40 cursor-pointer" data-testid={`pf-notif-${g.key}`}>
                <div className="text-sm text-[color:var(--aii-text-1)]">{g.label}</div>
                <button type="button" onClick={() => toggleNotifGroup(g.key)} disabled={notifBusy}
                  data-testid={`pf-notif-toggle-${g.key}`}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${g.on ? "bg-brand" : "bg-gray-300"} ${notifBusy ? "opacity-60" : ""}`}
                  aria-pressed={g.on}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${g.on ? "translate-x-6" : "translate-x-1"}`}/>
                </button>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
